import { useDraggable, useDroppable } from '@dnd-kit/react';
import type React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { BoardTileFace } from '../board/board-tile-face';
import type { TileData } from '../board/board-types';
import type { TileDragData, TileDropData } from '../board/tile-drag-overlay';
import styles from './tile-rack.module.scss';

/** A placeholder that fills an empty slot — an outline only, matching Board's origin ghost. */
const ghostTile = (id: string): TileData => ({ id, row: 0, col: 0, letter: '', dir: 'h', points: 0, ghost: true });

interface RackTileProps {
  tile: TileData;
  cellSize: number;
  gap: number;
  /** The owning rack's id — what the overlay compares a drop target against. */
  region: string;
  showPoints: boolean;
  draggable: boolean;
  isDragging: boolean;
  onKeyDown: (e: React.KeyboardEvent, tile: TileData) => void;
  onClick?: (tile: TileData) => void;
  /** Appended to the tile's accessible name, saying what activating it does. */
  activateLabel?: string;
}

/**
 * The tile occupying a slot, wired to dnd-kit's pointer drag.
 *
 * Mirrors `DraggableBoardTile`, `isDropping` included: the node stays mounted and in place during a
 * drag, presenting as the dashed ghost, while the `TileDragOverlay` renders the tile following the
 * pointer — then hides for the drop animation, so only the tile in flight is visible.
 */
const RackTile: React.FC<RackTileProps> = ({
  tile,
  cellSize,
  gap,
  region,
  showPoints,
  draggable,
  isDragging,
  onKeyDown,
  onClick,
  activateLabel,
}) => {
  const { ref, isDropping } = useDraggable<TileDragData>({
    id: tile.id,
    data: { tile, cellSize, gap, region, showPoints },
    disabled: !draggable,
  });

  // The tile stays ghosted from pickup until the drop animation has finished. `isDragging` ends the
  // moment the drag does, but `isDropping` only arrives a render later; without this the real tile is
  // drawn in that gap, alongside the overlay still flying in — a flash that a slow device makes visible.
  const [settling, setSettling] = useState(false);
  const sawDropping = useRef(false);
  useLayoutEffect(() => {
    if (isDragging) {
      sawDropping.current = false;
      setSettling(true);
      return;
    }
    if (isDropping) {
      sawDropping.current = true;
      return;
    }
    if (sawDropping.current) {
      sawDropping.current = false;
      setSettling(false);
      return;
    }
    // No drop animation is coming (reduced motion, or the tile left the rack), so nothing to wait for.
    const frame = requestAnimationFrame(() => setSettling(false));
    return () => cancelAnimationFrame(frame);
  }, [isDragging, isDropping]);

  const points = showPoints ? `, ${tile.points} point${tile.points === 1 ? '' : 's'}` : '';
  const ariaLabel = `Tile ${tile.letter.split('').join('-')}${points}, in rack${activateLabel ? `, ${activateLabel}` : ''}`;
  const rendered = isDragging || settling ? { ...tile, ghost: true } : tile;

  return (
    <BoardTileFace
      ref={ref}
      tile={rendered}
      cellSize={cellSize}
      gap={gap}
      showPoints={showPoints}
      role="button"
      tabIndex={draggable ? 0 : -1}
      aria-grabbed={isDragging || undefined}
      aria-label={ariaLabel}
      className={draggable ? styles.draggable : undefined}
      style={isDropping ? { opacity: 0 } : undefined}
      onKeyDown={(e) => onKeyDown(e, tile)}
      onClick={onClick && draggable ? () => onClick(tile) : undefined}
    />
  );
};

export interface TileRackSlotProps {
  slotId: string;
  /** The owning rack's id, shared by every one of its slots. */
  region: string;
  tile: TileData | null;
  cellSize: number;
  gap: number;
  showPoints: boolean;
  draggable: boolean;
  isDragging: boolean;
  /**
   * Ref for the slot's contents, which the rack slides from the slot the tile came from. It goes on
   * the inner element rather than the droppable itself so that the animating transform can never be
   * mistaken for the drop target's real position — see `useTileSlide`.
   */
  slideRef?: (element: HTMLElement | null) => void;
  onKeyDown: (e: React.KeyboardEvent, tile: TileData) => void;
  onClick?: (tile: TileData) => void;
  activateLabel?: string;
}

/**
 * One slot of the rack: a droppable region that holds a draggable tile, or nothing. Every slot is
 * droppable regardless of `draggable` — the rack's own tiles may be pickup-disabled while it still
 * accepts a tile dragged in from elsewhere (e.g. returned from the board).
 */
export const TileRackSlot: React.FC<TileRackSlotProps> = ({
  slotId,
  region,
  tile,
  cellSize,
  gap,
  showPoints,
  draggable,
  isDragging,
  slideRef,
  onKeyDown,
  onClick,
  activateLabel,
}) => {
  const { ref } = useDroppable<TileDropData>({ id: slotId, data: { region } });

  return (
    <div ref={ref} className={styles.slot}>
      <div ref={slideRef} className={styles.slotContent}>
        {tile ? (
          <RackTile
            tile={tile}
            cellSize={cellSize}
            gap={gap}
            region={region}
            showPoints={showPoints}
            draggable={draggable}
            isDragging={isDragging}
            onKeyDown={onKeyDown}
            onClick={onClick}
            activateLabel={activateLabel}
          />
        ) : (
          <BoardTileFace aria-hidden="true" tile={ghostTile(slotId)} cellSize={cellSize} gap={gap} />
        )}
      </div>
    </div>
  );
};
